const authService = {
  generateCodeVerifier() {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode(...array))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  },

  async generateCodeChallenge(verifier) {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const hash = await crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(hash)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  },

  async login() {
    // Skip authentication in dev mode
    if (window.CONFIG.DEV_MODE) {
      localStorage.setItem('dev_mode', 'true');
      localStorage.setItem('dev_user_id', 'dev-user-' + Date.now());
      window.location.href = '/chat';
      return;
    }

    const { COGNITO_DOMAIN, COGNITO_APP_CLIENT_ID, REDIRECT_URI } = window.CONFIG;
    
    const codeVerifier = this.generateCodeVerifier();
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);
    
    sessionStorage.setItem('code_verifier', codeVerifier);
    
    const authUrl = `${COGNITO_DOMAIN}/oauth2/authorize?` +
      `client_id=${COGNITO_APP_CLIENT_ID}&` +
      `response_type=code&` +
      `scope=openid+profile+email&` +
      `redirect_uri=${encodeURIComponent(REDIRECT_URI)}&` +
      `code_challenge=${codeChallenge}&` +
      `code_challenge_method=S256&` +
      `identity_provider=IdentityCenter`;
    window.location.href = authUrl;
  },

  async handleCallback(code) {
    const { COGNITO_DOMAIN, COGNITO_APP_CLIENT_ID, REDIRECT_URI } = window.CONFIG;
    const tokenUrl = `${COGNITO_DOMAIN}/oauth2/token`;
    const codeVerifier = sessionStorage.getItem('code_verifier');
    
    if (!codeVerifier) throw new Error('Code verifier not found');
    
    const params = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: COGNITO_APP_CLIENT_ID,
      code: code,
      redirect_uri: REDIRECT_URI,
      code_verifier: codeVerifier
    });

    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params
    });

    if (!response.ok) throw new Error('Token exchange failed');
    
    const data = await response.json();
    sessionStorage.removeItem('code_verifier');
    
    // Store tokens - group check is handled by Lambda authorizer
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('id_token', data.id_token);
    return data;
  },

  getToken() {
    // In dev mode, return a mock token
    if (window.CONFIG.DEV_MODE && localStorage.getItem('dev_mode') === 'true') {
      return 'dev-mode-token';
    }
    return localStorage.getItem('id_token');
  },

  getUserId() {
    // In dev mode, return the dev user ID
    if (window.CONFIG.DEV_MODE && localStorage.getItem('dev_mode') === 'true') {
      return localStorage.getItem('dev_user_id') || 'dev-user';
    }

    const token = this.getToken();
    if (!token) return null;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return (payload.email || payload.sub).replace(/[@.]/g, '_');
    } catch (e) {
      console.error('Failed to parse token:', e);
      return null;
    }
  },

  getDisplayName() {
    if (window.CONFIG.DEV_MODE && localStorage.getItem('dev_mode') === 'true') {
      return 'Developer';
    }

    const token = this.getToken();
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      // Try common JWT claims — always return just the first name
      if (payload.given_name) return payload.given_name.split(' ')[0];
      if (payload.name) return payload.name.split(' ')[0];
      if (payload.email) {
        const local = payload.email.split('@')[0];
        // Take only the first part before any separator (dot, underscore, dash)
        const firstName = local.split(/[._-]/)[0];
        return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
      }
      return null;
    } catch (e) {
      console.error('Failed to parse token for display name:', e);
      return null;
    }
  },

  isTokenExpired() {
    const token = localStorage.getItem('id_token');
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (!payload.exp) return true;
      // Expired if current time is past exp (with 60s buffer for clock skew)
      return Date.now() >= (payload.exp * 1000) - 60000;
    } catch (e) {
      return true;
    }
  },

  clearTokens() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
  },

  isAuthenticated() {
    // In dev mode, always return true if dev_mode flag is set
    if (window.CONFIG.DEV_MODE && localStorage.getItem('dev_mode') === 'true') {
      return true;
    }
    if (!localStorage.getItem('id_token')) return false;
    if (this.isTokenExpired()) {
      this.clearTokens();
      return false;
    }
    return true;
  },

  logout() {
    // In dev mode, just clear local storage and redirect
    if (window.CONFIG.DEV_MODE) {
      localStorage.removeItem('dev_mode');
      localStorage.removeItem('dev_user_id');
      sessionStorage.clear();
      window.location.href = '/';
      return;
    }

    const { COGNITO_DOMAIN, COGNITO_APP_CLIENT_ID } = window.CONFIG;
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    sessionStorage.clear();
    const logoutUri = encodeURIComponent(window.location.origin);
    window.location.href = `${COGNITO_DOMAIN}/logout?client_id=${COGNITO_APP_CLIENT_ID}&logout_uri=${logoutUri}`;
  }
};

export default authService;
